import { useState, useCallback } from 'react';

import { AppUtils } from '@/utils/app.utils';

import type { CreatePlantPayload } from '@/interfaces';

export interface UsePlantFormProps {
  initialState?: Partial<CreatePlantPayload>;
  onSubmit: (payload: CreatePlantPayload) => Promise<void> | void;
  companyId: number;
}

export const usePlantForm = ({ initialState, onSubmit, companyId }: UsePlantFormProps) => {
  const defaultInitialState: CreatePlantPayload = {
    identifier: '',
    alias: '',
    companyId,
    users: [],
  };

  const [form, setForm] = useState<CreatePlantPayload>(() => ({
    ...defaultInitialState,
    ...initialState,
  }));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof CreatePlantPayload, string>>>({});

  const updateField = useCallback(
    <K extends keyof CreatePlantPayload>(field: K, value: CreatePlantPayload[K]) => {
      setForm((prev) => ({ ...prev, [field]: value }));

      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    },
    [errors]
  );

  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof CreatePlantPayload, string>> = {};

    const cupsValidation = AppUtils.validateCupsStructure(form.identifier);

    if (!form.identifier) {
      newErrors.identifier = 'Identifier is required';
    } else if (!cupsValidation.isValid) {
      newErrors.identifier = cupsValidation.error;
    }

    if (!form.alias) {
      newErrors.alias = 'Alias is required';
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  }, [form]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    await onSubmit(form);

    setIsSubmitting(false);
  }, [validateForm, onSubmit, form]);

  return {
    form,
    isSubmitting,
    errors,
    updateField,
    updateUsers: (value: { id: number; value: string; label: string }[]) => updateField('users', value),
    handleSubmit,
  };
};

export default usePlantForm;
